import axios from "axios";

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:8080";
// `baseURL` is prepended to URLs passed to axios
axios.defaults.baseURL = API_URL;

// POST with JSON content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// `xsrfCookieName` is the name of the cookie
// to use as a value for xsrf token.
// Default is 'XSRF-TOKEN'
axios.defaults.xsrfCookieName = "CSRF-TOKEN";

// `xsrfHeaderName` is the name of the http header
// that carries the xsrf token value.
// Default is 'X-XSRF-TOKEN'
axios.defaults.xsrfHeaderName = "X-CSRF-Token";

// `withCredentials` indicates whether or not cross-site
// Access-Control requests should be made using credentials.
// axios.defaults.withCredentials = true;

const instance = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  xsrfCookieName: "CSRF-TOKEN",
  xsrfHeaderName: "X-CSRF-Token",
});

export default {
  getLeagueJSON(id) {
    return instance.get(`/leagues/${id}/export`);
  },
  getAdminSummary(id) {
    return instance.get(`/admin/summary`);
  },
  getUsers() {
    return instance.get(`/dropshadows`);
  },
  updateUser(user) {
    return instance.post(`/users/self`, user);
  },
  switchUserLocale(locale) {
    return instance.get(`/switch_locale/${locale}`);
  },
  checkSession() {
    return instance.get(`/users/self`);
  },
  getBalance(id) {
    return instance.get(`/schedules/${id}/balance`);
  },
  checkConflicts(schedule_id, season_id) {
    return instance.get(`/conflicts?schedule_id=${schedule_id}&season_id=${season_id}`);
  },
  getMasterBalance(season_id) {
    return instance.get(`/schedules/master/balance/${season_id}`);
  },
  exportScheduleSportsengine(schedule_id) {
    return instance.post(`/sportsengine/export/${schedule_id}`);
  },
  exportScheduleSportsEngineBeta(schedule_id) {
    return instance.post(`/sportsengine/export_beta/${schedule_id}`);
  },
  deleteAllSportsEngine(subseason_id) {
    return instance.post(`/sportsengine/clear/${subseason_id}`);
  },
  updateScheduleTotals(schedule_id) {
    return instance.get(`/schedules/${schedule_id}/update_totals`);
  },
  updateSeason(season) {
    return instance.patch(`/seasons/${season.id}`, season);
  },
  deleteSeason(season) {
    return instance.delete(`/seasons/${season.id}`, season);
  },
  bulkDeleteTimeslots(ids) {
    return instance.post(`/timeslots/bulk_delete`, ids);
  },
  updateLeague(league) {
    return instance.patch(`/leagues/${league.id}`, league);
  },
  deleteLeague(league) {
    return instance.delete(`/leagues/${league.id}`, league);
  },
  stripeBilling() {
    return instance.post(`/stripe/billing`);
  },
  stripeCheckout(plan) {
    return instance.post(`/stripe/checkout/${plan}`);
  },
  finishWizard(wizardInfo) {
    return instance.post(`/wizard`, wizardInfo);
  },
  importLeague(url) {
    return instance.post(`/import/league`, { url: url });
  },
  importSportsEngine(league_id, season_id, subseason_id, games, practices, coaches) {
    return instance.get(
      `/sportsengine/import/${league_id}/${season_id}/${subseason_id}?games=${games}&coaches=${coaches}&practices=${practices}`,
    );
  },
  importSportsEngineBeta(site_id, season_name, includeGames, includePractices, includeCoaches) {
    return instance.post(`/sportsengine/import/${site_id}/${encodeURIComponent(season_name)}`, {
      include_games: includeGames,
      include_practices: includePractices,
      include_coaches: includeCoaches,
    });
  },
  getSportsEngineAccessToken(code, timezone) {
    return instance.post(`/oauth/sportsengine?code=${code}`, { timezone: timezone });
  },
  getSESites() {
    return instance.get(`/sportsengine/sites`);
  },
  getSESitesBeta() {
    return instance.get("/sportsengine/sites_beta");
  },
  getSESeasonsBeta(site_id) {
    return instance.get(`/sportsengine/sites/${site_id}/seasons_beta`);
  },
  getSESchedules() {
    return instance.get(`/sportsengine/schedules`);
  },
  getSELeagues(site_id) {
    return instance.get(`/sportsengine/sites/${site_id}/leagues`);
  },
  getSESeasons(league_id) {
    return instance.get(`/sportsengine/leagues/${league_id}/seasons`);
  },
  getSESeason(season_id) {
    return instance.get(`/sportsengine/seasons/${season_id}`);
  },
  refreshItems(season_id, ids) {
    return instance.post(`/schedule_items/refresh?season_id=${season_id}`, { ids });
  },
  createScheduleItem(season_id, args) {
    return instance.post(`/schedule_items?season_id=${season_id}`, { args });
  },
  updateScheduleItem(season_id, args) {
    return instance.patch(`/schedule_items/${args.id}?season_id=${season_id}`, { args });
  },
  dryRunScheduleItem(season_id, args) {
    return instance.post(`/schedule_items/${args.id}/dry_run?season_id=${season_id}`, { args });
  },
  splitScheduleItem(id) {
    return instance.post(`/schedule_items/split/${id}`);
  },
  updateCalendarItem(season_id, args) {
    return instance.post(`/schedule_items/${args.id}/calendar?season_id=${season_id}`, { args });
  },
  createBulkTimeslots(season_id, args) {
    return instance.post(`/timeslots/bulk?season_id=${season_id}`, { args });
  },
  updateSeasonStuff(season) {
    return instance.post(`/seasons/bulk`, { season });
  },
  createBulkPeople(season_id, args) {
    return instance.post(`/people/bulk?season_id=${season_id}`, { args });
  },
  createBulkVenues(season_id, args) {
    return instance.post(`/venues/bulk?season_id=${season_id}`, { args });
  },
  createBulkDivisions(season_id, args) {
    return instance.post(`/divisions/bulk?season_id=${season_id}`, { args });
  },
  createBulkTeams(season_id, args) {
    return instance.post(`/teams/bulk?season_id=${season_id}`, { args });
  },
  copySeason(args) {
    return instance.post(`/seasons/copy`, { args });
  },
  createSeason(args) {
    console.log("API create season", args);
    return instance.post(`/seasons`, { args });
  },
  swapScheduleItems(args) {
    return instance.post(`/schedule_items/swap`, { args });
  },
  unscheduleTimeslots(season_id, timeslot_ids) {
    return instance.post(`/timeslots/unschedule`, { season_id, timeslot_ids });
  },
  unscheduleItems(args) {
    return instance.post(`/schedule_items/unschedule`, { args });
  },
  deleteScheduleItems(args) {
    return instance.post(`/schedule_items/delete`, { args });
  },
  moveScheduleItems(args) {
    return instance.post(`/schedule_items/move`, { args });
  },
  getMasterSchedule(season_id) {
    return instance.get(`/schedules/master/${season_id}`);
  },
  clearSeasonTimeslots(season_id) {
    return instance.post(`/timeslots/${season_id}/clear`);
  },
  clearTimeslots(season_id, timeslot_ids) {
    console.log("CLEAR TIMESLOTS", timeslot_ids);
    return instance.post(`/timeslots/${season_id}/clear_bulk`, { timeslot_ids: timeslot_ids });
  },
  copyTimeslots(season_id, items, copyDate, copyFrequency) {
    return instance.post(`/timeslots/copy_new`, {
      season_id: season_id,
      items: items,
      copyDate: copyDate,
      copyFrequency: copyFrequency,
    });
  },
  shiftTimeslots(season_id, items, moveChoices) {
    return instance.post(`/timeslots/shift`, { season_id: season_id, items: items, moveChoices: moveChoices });
  },
  splitTimeslots(season_id, items) {
    return instance.post(`/timeslots/split`, { season_id: season_id, items: items });
  },
  updateTimeslots(season_id, items, division_ids, is_game, is_practice, is_reserved) {
    return instance.post(`/timeslots/divisions`, {
      season_id: season_id,
      items: items,
      division_ids: division_ids,
      is_game: is_game,
      is_practice: is_practice,
      is_reserved: is_reserved,
    });
  },
  updateTimeslotSurface(season_id, items, surface_id) {
    return instance.post(`/timeslots/surfaces`, { season_id: season_id, items: items, surface_id: surface_id });
  },
  copyWeekTimeslots(details) {
    return instance.post(`/timeslots/copy`, details);
  },
  deleteTimeslot(timeslot_id) {
    return instance.delete(`/timeslots/${timeslot_id}`);
  },
  updateTimeslot(timeslot) {
    return instance.patch(`/timeslots/${timeslot.id}`, { timeslot });
  },
  updateMultipleTimeslots(timeslots) {
    return instance.post(`/timeslots/multiple`, { timeslots });
  },
  createTimeslot(timeslot) {
    return instance.post(`/timeslots`, { timeslot });
  },
  pasteTimeslots(timeslots, date) {
    return instance.post(`/timeslots/paste`, { timeslots: timeslots, date: date });
  },
  getLeagueInformation() {
    return instance.get(`/leagues?season_id=${this.currentSeason ? this.currentSeason.id : ""}`);
  },
  getOrganizationInformation() {
    return instance.get(`/organizations`);
  },
  switchOrganization(organization_id) {
    return instance.get(`/switch_organization/${organization_id}`);
  },
  getOrganization(organization_id) {
    return instance.get(`/organizations/${organization_id}`);
  },
  updateOrganization(organization) {
    return instance.patch(`/organizations/${organization.id}`, { organization });
  },
  getActivity(activity_id) {
    return instance.get(`/activities/${activity_id}`);
  },
  getSeasonTimeslots(season_id) {
    console.log("👑 Get Season Timeslots: " + season_id);
    return instance.get(`/seasons/${season_id}/timeslots`);
  },
  getSeasonItems(season_id) {
    console.log("👑 Get Season Items: " + season_id);
    return instance.get(`/seasons/${season_id}/items`);
  },
  getDivisionTimeslots(division_id, start, finish) {
    console.log("👑 Get Division Timeslots: " + division_id);
    return instance.get(`/timeslots/division/${division_id}?start=${start}&finish=${finish}`);
  },
  getScheduleList(season_id) {
    return instance.get(`/seasons/${season_id}/schedules`);
  },
  getEmbed(id) {
    return instance.get(`/embed/${id}`);
  },
  getSchedule(id) {
    return instance.get(`/schedules/${id}`);
  },
  getScheduleStatus(id) {
    return instance.get(`/schedules/${id}/status`);
  },
  cancelSchedule(id) {
    return instance.post(`/schedules/${id}/cancel`);
  },
  createSchedule(schedule_config) {
    return instance.post(`/schedule_configs`, { schedule_config });
  },
  createBlankSchedule(division_id) {
    return instance.post(`/schedules/blank?division_id=` + division_id);
  },
  updateScheduleConfig(schedule_config) {
    return instance.patch(`/schedule_configs/${schedule_config.id}`, { schedule_config });
  },
  createVenueTeam(team_id, venue_id) {
    return instance.post(`/venues/${venue_id}/venue_teams`, { team_id });
  },
  removeVenueTeam(venue_team) {
    return instance.delete(`/venue_teams/${venue_team.id}`);
  },
  createTeamTimeConstraint(team_id, constraint, applyAll) {
    return instance.post(`/teams/${team_id}/time_constraints`, { constraint: constraint, applyAll: applyAll });
  },
  removeTimeConstraint(constraint) {
    return instance.delete(`/time_constraints/${constraint.id}`);
  },
  createTeamTravelConstraint(team_id, constraint, applyAll) {
    return instance.post(`/teams/${team_id}/travel_constraints`, { constraint: constraint, applyAll: applyAll });
  },
  updateTravelConstraint(constraint_id, constraint, applyAll) {
    return instance.patch(`/travel_constraints/${constraint_id}`, { constraint: constraint, applyAll: applyAll });
  },
  createTeamPerson(team_id, person_id, role) {
    return instance.post(`/teams/${team_id}/team_people`, { person_id, role });
  },
  removeTeamPerson(team_person) {
    return instance.delete(`/team_people/${team_person.id}`);
  },

  updateSchedule(schedule) {
    return instance.patch(`/schedules/${schedule.id}`, schedule);
  },
  deleteSchedule(schedule) {
    return instance.delete(`/schedules/${schedule.id}?se=${schedule.se}`);
  },
  getSeasonSchedules(season_id) {
    return instance.get(`/schedules/?season_id=${season_id}`);
  },
  // Teams
  createTeams(teams) {
    return instance.post(`/teams`, { teams });
  },
  getTeam(id, season_id) {
    return instance.get(`/teams/${id}?season_id=${season_id}`);
  },
  getSeasonTeams(season_id) {
    return instance.get(`/teams/season_teams?season_id=${season_id}`);
  },
  updateTeam(team) {
    return instance.patch(`/teams/${team.id}`, team);
  },
  deleteTeam(team_id) {
    return instance.delete(`/teams/${team_id}`);
  },

  // Divisions
  getDivisions(season_id) {
    let path = "/divisions";
    if (season_id) path += `?season_id=${season_id}`;

    return instance.get(path);
  },
  getPublicDivisions(season_id) {
    return instance.get(`/public_divisions/${season_id}`);
  },
  createDivision(division) {
    return instance.post(`/divisions`, division);
  },
  getDivision(id, season_id) {
    return instance.get(`/divisions/${id}?season_id=${season_id}`);
  },
  updateDivision(division) {
    return instance.patch(`/divisions/${division.id}`, division);
  },
  deleteDivision(division_id) {
    return instance.delete(`/divisions/${division_id}`);
  },

  // Conferences
  getConferences(season_id) {
    return instance.get(`/conferences?season_id=${season_id}`);
  },
  createConference(conference) {
    return instance.post(`/conferences`, conference);
  },
  deleteConference(conference_id) {
    return instance.delete(`/conferences/${conference_id}`);
  },

  // Scoring System
  updateScoringSystem(systemId, system) {
    return instance.patch(`/scoring_systems/${systemId}`, system);
  },

  // Venues
  createVenue(venue) {
    return instance.post(`/venues`, venue);
  },
  createVenues(venues) {
    return instance.post("/venues/create_venues", { venues });
  },
  getVenue(id, season_id) {
    return instance.get(`/venues/${id}?season_id=${season_id}`);
  },
  updateVenue(venue) {
    return instance.patch(`/venues/${venue.id}`, venue);
  },
  deleteVenue(id) {
    return instance.delete(`/venues/${id}`);
  },

  // Games
  updateGame(game) {
    return instance.patch(`/games/${game.id}`, game);
  },
  updateGames(games) {
    return instance.patch("/games/update_multiple", { games });
  },

  // Public Webpage
  getCurrentPublicWebpage() {
    return instance.get("/public_webpage");
  },
  getCurrentPublicLeaguePage(slug) {
    return instance.get(`/public_league_pages/${slug}`);
  },
  getPublicWebpageByDomain(url) {
    return instance.get(`/webpage_by_domain?url=${url}`);
  },
  updateWebpage(webpage) {
    return instance.patch(`/public_webpages/${webpage.id}`, webpage, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  updateLeaguePageSeason(slug, leaguePage) {
    return instance.patch(`/public_league_pages/${slug}`, leaguePage, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  updateLeaguePageDisplay(slug){
    return instance.post(`/public_league_pages/${slug}/toggle_published`);
  },
  // People
  createPerson(person) {
    return instance.post(`/people`, person);
  },
  getPerson(id, season_id) {
    return instance.get(`/people/${id}?season_id=${season_id}`);
  },
  updatePerson(person) {
    return instance.patch(`/people/${person.id}`, person);
  },
  deletePerson(id) {
    return instance.delete(`/people/${id}`);
  },
  getPeople(season_id) {
    return instance.get(`/people?season_id=${season_id}`);
  },

  forgotPassword(email) {
    return instance.post(`/users/password/forgot`, { email: email });
  },
  resetPassword(token, password) {
    return instance.post(`/users/password/reset`, { token: token, password: password });
  },
  acceptInvitation(token) {
    return instance.get(`/accept_invitation/${token}`);
  },
  cancelInvitation(id) {
    return instance.delete(`/invitations/${id}`);
  },
  getOrganizationUsers(organization_id) {
    return instance.get(`/organizations/${organization_id}/organization_users`);
  },
  getOrganizationUser(organization_id, id) {
    return instance.get(`/organizations/${organization_id}/organization_users/${id}`);
  },
  createOrganizationUser(organization_id, organization_user) {
    return instance.post(`/organizations/${organization_id}/organization_users`, {
      organization_user: organization_user,
    });
  },
  removeOrganizationUser(id) {
    return instance.delete(`/organization_users/${id}`);
  },
  updateOrganizationUser(organization_id, id, role) {
    return instance.patch(`/organizations/${organization_id}/organization_users/${id}`, { role: role });
  },
  getSeason(id) {
    return instance.get(`/seasons/${id}`);
  },
  // createSeason(name, league_id) {
  //   return instance.post(`leagues/${league_id}/seasons`, { name });
  // },
  createLeague(name, seasonName) {
    return instance.post(`/leagues`, { name: name, seasonName: seasonName });
  },
  getLeagues() {
    return instance.get("/leagues");
  },
  getLeague(id) {
    return instance.get(`/leagues/${id}`);
  },
  createSession(email, password) {
    return instance.post("/session", {
      user: {
        email: email,
        password: password,
      },
    });
  },
  deleteSession() {
    return instance.delete("/session", {});
  },
  createUser(first, last, organization, email, password, timezone, offset, locale) {
    return instance.post("/user", {
      user: {
        first: first,
        last: last,
        organization: organization,
        email: email,
        password: password,
        timezone: timezone,
        timezone_offset: offset,
        locale: locale,
      },
    });
  },
  createTempUser(user) {
    return instance.post("/oauth/create_temp_user", { user });
  },
  confirmUserByToken(token) {
    return instance.get(`/user/confirmation?confirmation_token=${token}`);
  },
  resendConfirmationEmail(email) {
    return instance.post("/user/confirmation", { user: { email } });
  },
};

// Intercept all 401 Unauthorized responses before
// they are handled by `then` or `catch`

import store from "./store";
import router from "./router";

axios.interceptors.response.use(
  function(response) {
    console.log("Axios Response");
    console.log(response.request);
    return response;
  },
  function(error) {
    if (!error.response) {
      // network error
      // alert(`Unable to access API: ${error.message}`);
    } else if (error.response.status == 401) {
      // console.error("Unauthorized! Signing out...but not really");
      // store.dispatch("signOut").then(() => {
      //   router.replace("sign-in");
      // });
    }
    return Promise.reject(error);
  },
);
